import React, { useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  mergeClasses,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogBody,
  DialogActions,
  Button,
} from '@fluentui/react-components';
import {
  Dismiss20Regular,
} from '@fluentui/react-icons';
import {
  HiddenDivForAnnounce,
  ObjectSelectionImageView,
} from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import { useObjectSelectionStyles } from './style';

const DialogForObjectSelection = (props) => {
  const {
    i18n,
    open,
    setOpen,
    updateSubject,
    objectSelectionError,
  } = props;

  return (
    <Dialog
      modalType="modal"
      // this controls the dialog open state
      open={open}
      onOpenChange={(event, data) => {
        // it is the users responsibility to react accordingly to the open state change
        setOpen(data.open);
      }}
    >
      <DialogSurface aria-describedby={undefined}>
        <DialogBody>
          <DialogTitle action={<Button appearance="subtle" icon={<Dismiss20Regular />} onClick={() => setOpen(false)} />}>{i18n.getString(_TL_('Save before continuing?'))}</DialogTitle>
          <DialogContent>
            {i18n.getString(_TL_('Either save you changes or go back and keep editing'))}
          </DialogContent>
          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <Button
                appearance="primary"
                onClick={() => {
                  updateSubject();
                  setOpen(false);
                }}
                disabled={objectSelectionError && objectSelectionError !== ''}
              >
                {i18n.getString(_TL_('Save'))}
              </Button>
            </DialogTrigger>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary" onClick={() => setOpen(false)}>
                {i18n.getString(_TL_('Continue editing'))}
              </Button>
            </DialogTrigger>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export const ObjectSelectionView = ({
  i18n,
  originalImageUrl,
  originalImageHeight,
  originalImageWidth,
  getMaskValueAtPoint,
  updateHighlightedImage,
  isFetchingSegmentations,
  toggleSelectedMask,
  buttonsRef,
  borderImageSrc,
  updateSubject,
  workingSelectedImageSrc,
  highlightedImageSrc,
  objectSelectionError,
  maskValues,
  logger,
}) => {
  // get image from offscreen canvas
  // draw the canvas in the front
  const imageContainer = useRef(null);

  const [openEscape, setOpenEscape] = React.useState(false);

  const mouseOutsideHandler = useCallback((e) => {
    const imageWrapper = imageContainer.current && imageContainer.current.querySelector('.image-wrapper');
    if (imageWrapper && !imageWrapper.contains(e.target)
      && buttonsRef.current && !buttonsRef.current.contains(e.target)) {
      setOpenEscape(true);
    }
  }, [buttonsRef]);

  useEffect(() => {
    document.addEventListener('mousedown', mouseOutsideHandler);
    return () => {
      document.removeEventListener('mousedown', mouseOutsideHandler);
    };
  }, [mouseOutsideHandler]);

  const classes = useObjectSelectionStyles();

  return (
    <div className={mergeClasses(classes, 'objects-selection-view')} ref={imageContainer}>
      <ObjectSelectionImageView
        i18n={i18n}
        originalImageUrl={originalImageUrl}
        originalImageHeight={originalImageHeight}
        originalImageWidth={originalImageWidth}
        getMaskValueAtPoint={getMaskValueAtPoint}
        updateHighlightedImage={updateHighlightedImage}
        isFetchingSegmentations={isFetchingSegmentations}
        toggleSelectedMask={toggleSelectedMask}
        maskValues={maskValues}
        borderImageSrc={borderImageSrc}
        workingSelectedImageSrc={workingSelectedImageSrc}
        highlightedImageSrc={highlightedImageSrc}
        logger={logger}
      />
      <DialogForObjectSelection
        i18n={i18n}
        open={openEscape}
        setOpen={setOpenEscape}
        updateSubject={updateSubject}
        objectSelectionError={objectSelectionError}
      />
      <HiddenDivForAnnounce toAnnounce={i18n.getString(_TL_('Enter the objects selection view'))} />
    </div>
  );
};


ObjectSelectionView.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  originalImageUrl: PropTypes.string.isRequired,
  originalImageHeight: PropTypes.number.isRequired,
  originalImageWidth: PropTypes.number.isRequired,
  getMaskValueAtPoint: PropTypes.func.isRequired,
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
  }).isRequired,
  isFetchingSegmentations: PropTypes.bool.isRequired,
};

ObjectSelectionView.defaultProps = {
};
